
import { useState } from 'react';
import { Task } from '../../../hooks/usePomodoro';

interface TaskBoardProps {
  tasks: Task[];
  activeTaskId: string | null;
  onAddTask: (text: string) => void;
  onDeleteTask: (id: string) => void;
  onToggleComplete: (id: string) => void;
  onSetActiveTask: (id: string) => void;
}

export default function TaskBoard({
  tasks,
  activeTaskId,
  onAddTask,
  onDeleteTask,
  onToggleComplete,
  onSetActiveTask,
}: TaskBoardProps) {
  const [newTaskText, setNewTaskText] = useState('');
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const todoTasks = tasks.filter(task => !task.completed && task.id !== activeTaskId);
  const inProgressTasks = tasks.filter(task => task.id === activeTaskId && !task.completed);
  const completedTasks = tasks.filter(task => task.completed);

  const handleAddTask = (e: React.FormEvent) => {
    e.preventDefault();
    if (newTaskText.trim()) {
      onAddTask(newTaskText.trim());
      setNewTaskText('');
    }
  };

  const handleDragStart = (e: React.DragEvent, task: Task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, column: string) => {
    e.preventDefault();
    setDragOverColumn(column);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, column: string) => {
    e.preventDefault();
    setDragOverColumn(null);
    
    if (!draggedTask) return;

    if (column === 'inProgress') {
      onSetActiveTask(draggedTask.id);
    } else if (column === 'completed') {
      if (!draggedTask.completed) {
        onToggleComplete(draggedTask.id);
      }
    }
    
    setDraggedTask(null);
  };

  const TaskCard = ({ task, isDragging = false }: { task: Task; isDragging?: boolean }) => (
    <div
      draggable
      onDragStart={(e) => handleDragStart(e, task)}
      className={`bg-white rounded-lg p-3 shadow-sm border border-slate-200 cursor-move transition-all duration-200 hover:shadow-md ${
        isDragging ? 'opacity-50 transform rotate-2' : ''
      } ${task.id === activeTaskId ? 'ring-2 ring-blue-500 bg-blue-50' : ''} ${
        task.completed ? 'bg-green-50 border-green-200' : ''
      }`}
      onClick={() => !task.completed && onSetActiveTask(task.id)}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-medium text-slate-800 ${
            task.completed ? 'line-through text-slate-500' : ''
          }`}>
            {task.text}
          </p>
          {task.pomodorosCompleted > 0 && (
            <div className="flex items-center gap-1 mt-1">
              <i className="ri-time-fill text-xs text-slate-400"></i>
              <span className="text-xs text-slate-500">{task.pomodorosCompleted} pomodoros</span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!task.completed && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleComplete(task.id);
              }}
              className="w-6 h-6 flex items-center justify-center rounded bg-green-100 hover:bg-green-200 text-green-600 transition-colors"
            >
              <i className="ri-check-line text-xs"></i>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDeleteTask(task.id);
            }}
            className="w-6 h-6 flex items-center justify-center rounded bg-red-100 hover:bg-red-200 text-red-600 transition-colors"
          >
            <i className="ri-delete-bin-line text-xs"></i>
          </button>
        </div>
      </div>
    </div>
  );

  const Column = ({ 
    title, 
    tasks: columnTasks, 
    columnId, 
    color 
  }: { 
    title: string; 
    tasks: Task[]; 
    columnId: string; 
    color: string;
  }) => (
    <div
      className={`bg-slate-50 rounded-xl p-4 h-full ${
        dragOverColumn === columnId ? 'bg-blue-50 ring-2 ring-blue-300' : ''
      }`}
      onDragOver={(e) => handleDragOver(e, columnId)}
      onDragLeave={handleDragLeave}
      onDrop={(e) => handleDrop(e, columnId)}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-700 flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${color}`}></div>
          {title}
        </h3>
        <span className="text-sm text-slate-500 bg-white px-2 py-1 rounded-full">
          {columnTasks.length}
        </span>
      </div>
      
      <div className="space-y-2 max-h-[calc(100vh-300px)] overflow-y-auto">
        {columnTasks.map((task) => (
          <TaskCard 
            key={task.id} 
            task={task} 
            isDragging={draggedTask?.id === task.id}
          />
        ))}
        
        {columnTasks.length === 0 && (
          <div className="text-center py-8 text-slate-400">
            <i className={`ri-${columnId === 'todo' ? 'add' : columnId === 'inProgress' ? 'play' : 'check'}-line text-2xl mb-2 block`}></i>
            <p className="text-sm">
              {columnId === 'todo' ? 'Add your first task' : 
               columnId === 'inProgress' ? 'Drag a task here to start' : 
               'No completed tasks yet'}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-slate-200/50 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold text-slate-800">Task Board</h2>
        <div className="text-sm text-slate-500">
          {tasks.length} total tasks
        </div>
      </div>

      {/* Add Task Form */}
      <form onSubmit={handleAddTask} className="mb-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={newTaskText}
            onChange={(e) => setNewTaskText(e.target.value)}
            placeholder="Add a new task..."
            className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            maxLength={200}
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-1 whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            Add
          </button>
        </div>
      </form>

      {/* Kanban Columns */}
      <div className="grid grid-cols-3 gap-3 flex-1 min-h-0">
        <Column
          title="To-Do"
          tasks={todoTasks}
          columnId="todo"
          color="bg-slate-400"
        />
        <Column
          title="In Progress"
          tasks={inProgressTasks}
          columnId="inProgress"
          color="bg-blue-500"
        />
        <Column
          title="Completed"
          tasks={completedTasks}
          columnId="completed"
          color="bg-green-500"
        />
      </div>
    </div>
  );
}
